import React, { useState } from 'react';
import BottomSheet from './BottomSheet';
import { useToast } from '../contexts/ToastContext';
import { enablePush, getPushStatus } from '../lib/restPush';

/**
 * Pre-prompt notifiche mostrato al PRIMO timer di recupero della vita
 * dell'utente. Solo il tap su "Attiva notifiche" apre il prompt nativo
 * (requisito iOS: la richiesta deve partire da un gesto). Il chiamante
 * marca il pre-prompt come "fatto" alla chiusura, qualunque sia l'esito.
 */
function RestPushPrePrompt({ onClose }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  // Stato calcolato all'apertura: 'off' (si può chiedere) o 'need-install' (iOS senza PWA)
  const [status] = useState(getPushStatus);

  const handleActivate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // Timeout di sicurezza: se l'attivazione resta appesa (es. subscribe
      // che non risponde), l'utente non deve restare bloccato sullo sheet.
      await Promise.race([
        enablePush(), // prompt nativo: parte da questo tap
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('enablePush timeout')), 15000)
        ),
      ]);
      toast.success('Notifiche attivate ✓ Ti avviserò a fine recupero.');
    } catch (e) {
      if (e.message?.startsWith('permission-')) {
        // Negato o ignorato: nessun allarme, resta il toggle nel Profilo
        toast.info('Va bene. Puoi attivarle quando vuoi dal Profilo.');
      } else {
        toast.error('Non sono riuscito ad attivare le notifiche. Riprova dal Profilo.');
      }
    } finally {
      setBusy(false);
      onClose();
    }
  };

  const needInstall = status === 'need-install';

  return (
    <BottomSheet
      onClose={onClose}
      zIndexClass="z-[1300]"
      footer={
        needInstall ? (
          <button
            type="button"
            onClick={onClose}
            className="w-full py-3.5 rounded-2xl font-semibold text-base bg-primary-600 text-white"
          >
            Ok, capito
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={handleActivate}
              disabled={busy}
              className="w-full py-3.5 rounded-2xl font-semibold text-base bg-primary-600 text-white disabled:opacity-60"
            >
              {busy ? 'Attivazione…' : 'Attiva notifiche'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-full py-3 rounded-2xl font-semibold text-sm bg-white text-gray-600 border border-gray-200 active:bg-gray-50"
            >
              Non ora
            </button>
          </>
        )
      }
    >
      <div className="px-6 pt-3 pb-2 text-center">
        <div className="text-5xl mb-3">🔔</div>
        <h2 className="text-xl font-bold text-gray-900">
          Vuoi che ti avvisi quando termina il recupero, anche a schermo spento?
        </h2>
        {needInstall ? (
          <p className="text-sm text-amber-600 mt-3 leading-snug">
            Su iPhone le notifiche funzionano solo con l'app installata: apri Uptra in
            Safari → Condividi → "Aggiungi alla schermata Home", poi attivale dal Profilo.
          </p>
        ) : (
          <p className="text-sm text-gray-500 mt-2">
            Una notifica a fine recupero, solo quando l'app è in background. Puoi
            disattivarle in ogni momento dal Profilo.
          </p>
        )}
      </div>
    </BottomSheet>
  );
}

export default RestPushPrePrompt;
